// Auto-extracted tab: admin
import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * AdminTab - extracted from app.jsx
 * Original lines: 11154-13477
 * 
 * TODO: This component needs props passed from AppContent:
 * - darkMode, scenarios, thresholds, etc.
 * - All the state and handlers it uses
 */
export function AdminTab(props) {
  // Original render content (needs refactoring to use props):
  return (
    <>
          <div className="space-y-6">
            {/* Platform Admin Banner */}
            {isPlatformAdmin && (
              <div className={`rounded-xl p-6 ${darkMode ? "bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-700/50" : "bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200"}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">🛡️</span>
                  <div>
                    <h2 className={`text-lg font-bold ${darkMode ? "text-amber-400" : "text-amber-700"}`}>
                      Platform Administrator
                    </h2>
                    <p className={`text-sm ${darkMode ? "text-amber-300/70" : "text-amber-600"}`}>
                      You have system-wide access to all organizations and feeds
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-lg ${darkMode ? "bg-slate-800/50" : "bg-white"}`}>
                    <div className={`text-2xl font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>
                      {feedHealthStats.total || "—"}
                    </div>
                    <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Total Feeds</div>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? "bg-slate-800/50" : "bg-white"}`}>
                    <div className={`text-2xl font-bold ${darkMode ? "text-emerald-400" : "text-emerald-600"}`}>
                      {feedHealthStats.healthy || "—"}
                    </div>
                    <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Healthy Feeds</div>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? "bg-slate-800/50" : "bg-white"}`}>
                    <div className={`text-2xl font-bold ${darkMode ? "text-red-400" : "text-red-600"}`}>
                      {activeAlerts.length}
                    </div>
                    <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Active Alerts</div>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? "bg-slate-800/50" : "bg-white"}`}>
                    <div className={`text-2xl font-bold ${darkMode ? "text-blue-400" : "text-blue-600"}`}>
                      {feedHealthStats.itemsLast7Days || "—"}
                    </div>
                    <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Items (7d)</div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Non-admin notice */}
            {!isPlatformAdmin && (
              <div className={`rounded-xl p-6 ${darkMode ? "bg-slate-800 border border-slate-700" : "bg-slate-100 border border-slate-200"}`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">ℹ️</span>
                  <div>
                    <h3 className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>Organization Settings</h3>
                    <p className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>
                      Platform-wide feed management is restricted to platform administrators.
                      Contact support if you need system-level access.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* ============================================================ */}
            {/* EXAMINER SHAREABLE LINKS - Time-limited read-only access */}
            {/* ============================================================ */}
            <Card 
              darkMode={darkMode}
              title="🔗 Examiner Access Links" 
              subtitle="Share read-only access with regulators without requiring accounts"
              actions={
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={() => {
                    setShowCreateLinkModal(true);
                    loadExaminerLinks();
                  }}
                >
                  <Icons.Plus className="w-4 h-4 mr-1" />
                  Create Link
                </Button>
              }
            >
              <div className={`mb-4 p-3 rounded-lg ${darkMode ? "bg-blue-900/20 border border-blue-800/30" : "bg-blue-50 border border-blue-100"}`}>
                <p className={`text-sm ${darkMode ? "text-blue-300" : "text-blue-700"}`}>
                  <strong>How it works:</strong> Create a time-limited link that gives examiners read-only access to view your scenarios, thresholds, and attestations without needing to create an account or log in.
                </p>
              </div>
              
              {examinerLinks.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                  <Icons.Link className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No examiner links created yet</p>
                  <p className="text-sm mt-1">Click "Create Link" to generate a shareable URL</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {examinerLinks.map(link => {
                    const isExpired = new Date(link.expires_at) < new Date();
                    const isActive = link.is_active && !isExpired;
                    return (
                      <div 
                        key={link.id}
                        className={`p-4 rounded-xl flex items-center justify-between ${
                          darkMode 
                            ? "bg-slate-800 border border-slate-700" 
                            : "bg-slate-50 border border-slate-200"
                        } ${!isActive ? "opacity-60" : ""}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>
                              {link.name}
                            </span>
                            {!link.is_active && (
                              <Badge color="red">Revoked</Badge>
                            )}
                            {link.is_active && isExpired && (
                              <Badge color="amber">Expired</Badge>
                            )}
                            {isActive && (
                              <Badge color="emerald">Active</Badge>
                            )}
                          </div>
                          <div className={`text-xs mt-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                            Expires: {new Date(link.expires_at).toLocaleDateString()} · 
                            Accessed: {link.accessed_count} time{link.accessed_count !== 1 ? "s" : ""}
                            {link.last_accessed_at && ` · Last: ${new Date(link.last_accessed_at).toLocaleDateString()}`}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isActive && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => copyExaminerLink(link.token)}
                              aria-label="Copy link to clipboard"
                            >
                              <Icons.Copy className="w-4 h-4" />
                            </Button>
                          )}
                          {link.is_active && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => revokeExaminerLink(link.id)}
                              aria-label="Revoke this link"
                            >
                              <Icons.X className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Create Examiner Link Modal */}
            {showCreateLinkModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowCreateLinkModal(false)}>
                <div 
                  className={`w-full max-w-md rounded-2xl shadow-xl ${darkMode ? "bg-slate-900 border border-slate-700" : "bg-white"}`}
                  onClick={e => e.stopPropagation()}
                >
                  <div className={`px-6 py-4 border-b ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
                    <h3 className={`text-lg font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>
                      Create Examiner Link
                    </h3>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label htmlFor="link-name" className={`block text-sm font-medium mb-1 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                        Link Name *
                      </label>
                      <input
                        id="link-name"
                        type="text"
                        aria-label="Name for this examiner link"
                        aria-required="true"
                        placeholder="Q1 2026 OCC Exam"
                        value={newLinkForm.name}
                        onChange={e => setNewLinkForm(f => ({ ...f, name: e.target.value }))}
                        className={`w-full px-3 py-2 rounded-lg border ${
                          darkMode 
                            ? "bg-slate-800 border-slate-600 text-white placeholder-slate-500" 
                            : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                      <p className={`text-xs mt-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                        Use a descriptive name like "Q1 2026 FDIC Exam" or "OCC Annual Review"
                      </p>
                    </div>
                    
                    <div>
                      <label htmlFor="link-expiry" className={`block text-sm font-medium mb-1 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                        Expires In
                      </label>
                      <select
                        id="link-expiry"
                        aria-label="When this link should expire"
                        value={newLinkForm.expiresIn}
                        onChange={e => setNewLinkForm(f => ({ ...f, expiresIn: e.target.value }))}
                        className={`w-full px-3 py-2 rounded-lg border ${
                          darkMode 
                            ? "bg-slate-800 border-slate-600 text-white" 
                            : "bg-white border-slate-200 text-slate-900"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      >
                        <option value="1">1 day</option>
                        <option value="7">7 days</option>
                        <option value="14">14 days</option>
                        <option value="30">30 days</option>
                        <option value="90">90 days</option>
                      </select>
                    </div>
                    
                    <div className={`p-3 rounded-lg ${darkMode ? "bg-amber-900/20 border border-amber-800/30" : "bg-amber-50 border border-amber-100"}`}>
                      <p className={`text-xs ${darkMode ? "text-amber-300" : "text-amber-700"}`}>
                        <strong>Security note:</strong> Links provide read-only access. They can be revoked at any time and automatically expire. All access is logged.
                      </p>
                    </div>
                  </div>
                  <div className={`px-6 py-4 border-t flex justify-end gap-2 ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
                    <Button variant="secondary" onClick={() => setShowCreateLinkModal(false)}>
                      Cancel
                    </Button>
                    <Button onClick={createExaminerLink} disabled={!newLinkForm.name}>
                      Create & Copy Link
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Scheduled Email Reports */}
            <ScheduledReportsManager
              schedules={scheduledReports}
              darkMode={darkMode}
              onSave={(newSchedule) => {
                const updated = [...scheduledReports, { ...newSchedule, id: Date.now() }];
                setScheduledReports(updated);
                localStorage.setItem('steadfastly_scheduled_reports', JSON.stringify(updated));
                setToast({ type: 'success', message: `Report schedule "${newSchedule.name}" created` });
                setTimeout(() => setToast(null), 3000);
              }}
              onDelete={(index) => {
                const updated = scheduledReports.filter((_, i) => i !== index);
                setScheduledReports(updated);
                localStorage.setItem('steadfastly_scheduled_reports', JSON.stringify(updated));
                setToast({ type: 'success', message: 'Report schedule deleted' });
                setTimeout(() => setToast(null), 3000);
              }}
            />
            
            {/* Training Mode Controls */}
            <Card 
              darkMode={darkMode}
              title="🎓 Training Mode" 
              subtitle={trainingMode ? "Currently using sample training data" : "Training mode disabled - using production data"}
            >
              <div className="space-y-4">
                {/* Training Mode Status */}
                <div className={`p-4 rounded-xl ${
                  trainingMode 
                    ? (darkMode ? "bg-blue-900/30 border border-blue-700" : "bg-blue-50 border border-blue-200")
                    : (darkMode ? "bg-emerald-900/30 border border-emerald-700" : "bg-emerald-50 border border-emerald-200")
                }`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{trainingMode ? '📚' : '🚀'}</span>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {trainingMode ? 'Training Mode Active' : 'Production Mode'}
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-600'}`}>
                        {trainingMode 
                          ? 'Sample data is loaded for learning. Your work here won\'t affect production.'
                          : 'You\'re working with real compliance data.'}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* User Persona */}
                {userPersona && (
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <div className={`text-xs font-medium ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>Your Profile</div>
                    <div className={`flex items-center gap-2 mt-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      <span>{USER_PERSONAS.find(p => p.id === userPersona)?.icon}</span>
                      <span>{USER_PERSONAS.find(p => p.id === userPersona)?.label}</span>
                    </div>
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  {/* Restart Tutorial */}
                  <Button 
                    variant="secondary" 
                    onClick={() => {
                      setTutorialStep(0);
                      setShowTutorial(true);
                    }}
                  >
                    🎯 Restart Tutorial
                  </Button>
                  
                  {/* Reset Training Data - only in training mode */}
                  {trainingMode && (
                    <Button 
                      variant="secondary"
                      onClick={() => setShowResetTrainingModal(true)}
                    >
                      🔄 Reset Training Data
                    </Button>
                  )}
                  
                  {/* Go Live - only in training mode */}
                  {trainingMode && (
                    <Button 
                      onClick={() => setShowGoLiveModal(true)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      🚀 Go Live
                    </Button>
                  )}
                </div>
                
                {trainingMode && (
                  <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                    Click "Go Live" when you're ready to start your real compliance program. This will delete sample data but keep anything you created.
                  </p>
                )}
              </div>
            </Card>
            
            {/* Regulatory Feed Health Dashboard - Only for platform admins */}
            {isPlatformAdmin && (
            <>
            <Card 
              darkMode={darkMode}
              title="📡 Regulatory Feed Health" 
              subtitle="Monitor RSS feeds and manual sources for silent failures"
              actions={
                <div className="flex items-center gap-2">
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={loadFeedHealthData}
                    disabled={feedHealthLoading}
                  >
                    {feedHealthLoading ? <><Icons.Clock className="animate-spin" /> Checking...</> : <><Icons.Refresh /> Refresh</>}
                  </Button>
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={() => setShowFeedHealthDashboard(!showFeedHealthDashboard)}
                  >
                    {showFeedHealthDashboard ? "Hide Details" : "Show Details"}
                  </Button>
                </div>
              }
            >
              {/* Summary Stats Banner */}
              <div className={`rounded-xl p-4 mb-4 ${darkMode ? "bg-slate-800" : "bg-gradient-to-r from-slate-50 to-blue-50"}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                    Last checked: {feedHealthStats.lastChecked ? formatDate(feedHealthStats.lastChecked) : "Never"}
                  </div>
                  {feedHealthStats.failed > 0 && (
                    <Badge color="red">⚠️ {feedHealthStats.failed} Alert{feedHealthStats.failed !== 1 ? "s" : ""}</Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-center">
                  <div>
                    <div className={`text-2xl font-bold ${darkMode ? "text-emerald-400" : "text-emerald-600"}`}>
                      {feedHealthStats.healthy}
                    </div>
                    <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>🟢 Healthy</div>
                  </div>
                  <div>
                    <div className={`text-2xl font-bold ${darkMode ? "text-amber-400" : "text-amber-600"}`}>
                      {feedHealthStats.stale}
                    </div>
                    <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>🟡 Stale</div>
                  </div>
                  <div>
                    <div className={`text-2xl font-bold ${darkMode ? "text-red-400" : "text-red-600"}`}>
                      {feedHealthStats.failed}
                    </div>
                    <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>🔴 Failed</div>
                  </div>
                  <div>
                    <div className={`text-2xl font-bold ${darkMode ? "text-blue-400" : "text-blue-600"}`}>
                      {feedHealthStats.itemsLast7Days}
                    </div>
                    <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Items (7d)</div>
                  </div>
                  <div>
                    <div className={`text-2xl font-bold ${darkMode ? "text-indigo-400" : "text-indigo-600"}`}>
                      {feedHealthStats.pendingReview}
                    </div>
                    <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Pending</div>
                  </div>
                  <div>
                    <div className={`text-2xl font-bold ${darkMode ? "text-purple-400" : "text-purple-600"}`}>
                      {feedHealthStats.adoptedLast7Days}
                    </div>
                    <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Adopted (7d)</div>
                  </div>
                </div>
              </div>
              
              {/* Feed Status Table (expanded view) */}
              {showFeedHealthDashboard && (
                <div className="space-y-4">
                  {/* Load data on first expand */}
                  {feedHealthData.length === 0 && !feedHealthLoading && (
                    <div className="text-center py-8">
                      <Button variant="primary" onClick={loadFeedHealthData}>
                        Load Feed Status
                      </Button>
                    </div>
                  )}
                  
                  {feedHealthLoading && (
                    <div className="text-center py-8">
                      <Icons.Clock className="w-8 h-8 animate-spin mx-auto text-blue-500" />
                      <p className={`mt-2 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Loading feed status...</p>
                    </div>
                  )}
                  
                  {feedHealthData.length > 0 && (
                    <>
                      {/* RSS Feeds Section */}
                      <div>
                        <h4 className={`text-sm font-semibold mb-2 ${darkMode ? "text-white" : "text-slate-900"}`}>
                          RSS/Atom Feeds ({feedHealthData.filter(f => f.feed_type === 'rss' || f.feed_type === 'atom').length})
                        </h4>
                        <div className="overflow-x-auto">
                          <table className={`w-full text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                            <thead>
                              <tr className={darkMode ? "bg-slate-800" : "bg-slate-100"}>
                                <th className="text-left p-2">Feed</th>
                                <th className="text-left p-2">Jurisdiction</th>
                                <th className="text-left p-2">Last Success</th>
                                <th className="text-center p-2">Items (7d)</th>
                                <th className="text-center p-2">Status</th>
                                <th className="text-right p-2">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {feedHealthData.filter(f => f.feed_type === 'rss' || f.feed_type === 'atom').map(feed => {
                                const lastSuccess = feed.pollHistory?.find(h => h.success);
                                const itemsLast7d = feed.pollHistory?.reduce((sum, h) => sum + (h.items_new || 0), 0) || 0;
                                return (
                                  <tr key={feed.id} className={`border-b ${darkMode ? "border-slate-700 hover:bg-slate-800/50" : "border-slate-200 hover:bg-slate-50"}`}>
                                    <td className="p-2">
                                      <div className="font-medium">{feed.regulator_acronym || feed.name}</div>
                                      <div className={`text-xs truncate max-w-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>{feed.name}</div>
                                    </td>
                                    <td className="p-2">
                                      {JURISDICTION_CONFIG[feed.jurisdiction_code]?.flag || "🌐"} {feed.jurisdiction_code}
                                    </td>
                                    <td className="p-2">
                                      {lastSuccess ? (
                                        <span className={darkMode ? "text-slate-500" : "text-slate-500"}>
                                          {formatDate(lastSuccess.poll_started_at)}
                                        </span>
                                      ) : (
                                        <span className="text-red-500">Never</span>
                                      )}
                                    </td>
                                    <td className="p-2 text-center">{itemsLast7d}</td>
                                    <td className="p-2 text-center">
                                      <Badge color={feed.color} size="sm">
                                        {feed.status === 'healthy' ? '🟢' : feed.status === 'stale' ? '🟡' : '🔴'} {feed.label}
                                      </Badge>
                                    </td>
                                    <td className="p-2 text-right">
                                      <div className="flex justify-end gap-1">
                                        <Button variant="ghost" size="sm" onClick={() => testFeed(feed)}>Test</Button>
                                        <Button variant="ghost" size="sm" onClick={() => setSelectedFeedDetail(feed)}>Details</Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      
                      {/* Manual Sources Section */}
                      <div>
                        <h4 className={`text-sm font-semibold mb-2 ${darkMode ? "text-white" : "text-slate-900"}`}>
                          Manual Sources ({feedHealthData.filter(f => f.feed_type === 'manual' || f.feed_type === 'scrape').length})
                        </h4>
                        <div className="grid gap-2">
                          {feedHealthData.filter(f => f.feed_type === 'manual' || f.feed_type === 'scrape').map(feed => {
                            const lastCheck = feed.pollHistory?.[0];
                            return (
                              <div key={feed.id} className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? "bg-slate-800" : "bg-slate-50"}`}>
                                <div className="flex items-center gap-3">
                                  <span className="text-lg">{JURISDICTION_CONFIG[feed.jurisdiction_code]?.flag || "🌐"}</span>
                                  <div>
                                    <div className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>
                                      {feed.regulator_acronym || feed.name}
                                    </div>
                                    <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                                      Last checked: {lastCheck ? formatDate(lastCheck.poll_started_at) : "Never"}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge color={feed.color} size="sm">
                                    {feed.status === 'healthy' ? '🟢' : feed.status === 'stale' ? '🟡' : '🔴'} {feed.label}
                                  </Badge>
                                  <Button variant="secondary" size="sm" onClick={() => markManualSourceChecked(feed.id)}>
                                    Mark Checked
                                  </Button>
                                  <a 
                                    href={feed.url || feed.publications_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                  >
                                    <Button variant="ghost" size="sm">
                                      <Icons.ExternalLink className="w-4 h-4" />
                                    </Button>
                                  </a>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Status Legend */}
                      <div className={`text-xs p-3 rounded-lg ${darkMode ? "bg-slate-800 text-slate-500" : "bg-slate-100 text-slate-500"}`}>
                        <strong>Status Logic:</strong> 
                        🟢 RSS polled in 24h / Manual checked in 14d | 
                        🟡 RSS 24-72h stale / Manual 14-30d overdue | 
                        🔴 RSS failed 72h+ / Manual 30d+ critical
                      </div>
                    </>
                  )}
                </div>
              )}
            </Card>
            
            {/* Feed Detail Modal */}
            {selectedFeedDetail && (
              <Modal darkMode={darkMode} open={!!selectedFeedDetail} onClose={() => setSelectedFeedDetail(null)} title={`${selectedFeedDetail.regulator_acronym || selectedFeedDetail.name} Feed Details`} size="lg">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Type</div>
                      <div className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{selectedFeedDetail.feed_type?.toUpperCase()}</div>
                    </div>
                    <div>
                      <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Jurisdiction</div>
                      <div className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>
                        {JURISDICTION_CONFIG[selectedFeedDetail.jurisdiction_code]?.flag} {JURISDICTION_CONFIG[selectedFeedDetail.jurisdiction_code]?.name || selectedFeedDetail.jurisdiction_code}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>URL</div>
                      <div className={`text-sm break-all ${darkMode ? "text-blue-400" : "text-blue-600"}`}>
                        <a href={selectedFeedDetail.url} target="_blank" rel="noopener noreferrer">{selectedFeedDetail.url}</a>
                      </div>
                    </div>
                    <div>
                      <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Status</div>
                      <Badge color={selectedFeedDetail.color}>{selectedFeedDetail.label}</Badge>
                    </div>
                    <div>
                      <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Active</div>
                      <Badge color={selectedFeedDetail.is_active ? "emerald" : "slate"}>
                        {selectedFeedDetail.is_active ? "Yes" : "No"}
                      </Badge>
                    </div>
                  </div>
                  
                  <div>
                    <div className={`text-sm font-medium mb-2 ${darkMode ? "text-white" : "text-slate-900"}`}>Poll History (Last 10)</div>
                    <div className="space-y-1 max-h-64 overflow-y-auto">
                      {selectedFeedDetail.pollHistory?.length > 0 ? (
                        selectedFeedDetail.pollHistory.map((poll, idx) => (
                          <div key={idx} className={`flex items-center justify-between p-2 rounded ${darkMode ? "bg-slate-800" : "bg-slate-50"}`}>
                            <div className="flex items-center gap-2">
                              <span>{poll.success ? "✓" : "✗"}</span>
                              <span className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                                {formatDate(poll.poll_started_at)}
                              </span>
                            </div>
                            <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                              {poll.items_new || 0} new items
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>No poll history</div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button variant="primary" onClick={() => { testFeed(selectedFeedDetail); setSelectedFeedDetail(null); }}>
                      Test Now
                    </Button>
                    <a href={selectedFeedDetail.url} target="_blank" rel="noopener noreferrer">
                      <Button variant="secondary">
                        <Icons.ExternalLink className="w-4 h-4 mr-1" /> View Feed
                      </Button>
                    </a>
                  </div>
                </div>
              </Modal>
            )}
            </>
            )} {/* End isPlatformAdmin conditional for Feed Health + Modal */}
            
            {/* Alert Settings - Only for platform admins */}
            {isPlatformAdmin && (
            <Card 
              darkMode={darkMode}
              title="🔔 Alert Settings" 
              subtitle="Get notified when feeds fail - never miss a regulatory update"
              actions={
                <div className="flex items-center gap-2">
                  {activeAlerts.length > 0 && (
                    <Badge color="red">🚨 {activeAlerts.length} Active</Badge>
                  )}
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={() => { loadAlertSettings(); loadActiveAlerts(); setShowAlertSettings(!showAlertSettings); }}
                  >
                    {showAlertSettings ? "Hide" : "Configure"}
                  </Button>
                </div>
              }
            >
              {/* Active Alerts Banner */}
              {activeAlerts.length > 0 && (
                <div className={`mb-4 p-4 rounded-lg border ${darkMode ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"}`}>
                  <h4 className={`font-semibold mb-2 ${darkMode ? "text-red-400" : "text-red-700"}`}>
                    🚨 Active Alerts ({activeAlerts.length})
                  </h4>
                  <div className="space-y-2">
                    {activeAlerts.slice(0, 5).map(alert => (
                      <div key={alert.id} className={`flex items-center justify-between p-2 rounded ${darkMode ? "bg-slate-800" : "bg-white"}`}>
                        <div>
                          <div className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>
                            {alert.regulatory_feeds?.name || alert.feed_id}
                          </div>
                          <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                            {alert.message?.slice(0, 80)}...
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge color={alert.severity === "critical" ? "red" : "amber"} size="sm">
                            {alert.severity}
                          </Badge>
                          {!alert.acknowledged_at && (
                            <Button variant="ghost" size="sm" onClick={() => acknowledgeAlert(alert.id)}>
                              Ack
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => resolveAlert(alert.id)}>
                            Resolve
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {showAlertSettings && (
                <div className="space-y-6">
                  {/* Contact Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="alert-email" className={`block text-sm font-medium mb-1 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                        Alert Email
                      </label>
                      <input
                        id="alert-email"
                        type="email"
                        value={alertSettings.alert_email}
                        onChange={e => setAlertSettings(s => ({ ...s, alert_email: e.target.value }))}
                        placeholder="erik@woosterllc.com"
                        className={`w-full px-3 py-2.5 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${darkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-slate-200"}`}
                      />
                    </div>
                    <div>
                      <label htmlFor="alert-sms" className={`block text-sm font-medium mb-1 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                        Alert SMS (E.164 format)
                      </label>
                      <input
                        id="alert-sms"
                        type="tel"
                        value={alertSettings.alert_sms}
                        onChange={e => setAlertSettings(s => ({ ...s, alert_sms: e.target.value }))}
                        placeholder="+1 650-622-2525"
                        className={`w-full px-3 py-2.5 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${darkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-slate-200"}`}
                      />
                    </div>
                  </div>
                  
                  {/* Alert Toggles */}
                  <div className={`p-4 rounded-lg ${darkMode ? "bg-slate-800" : "bg-slate-50"}`}>
                    <h4 className={`font-medium mb-3 ${darkMode ? "text-white" : "text-slate-900"}`}>Notification Preferences</h4>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          aria-label="Enable SMS notifications on feed failure"
                          checked={alertSettings.sms_on_feed_failure}
                          onChange={e => setAlertSettings(s => ({ ...s, sms_on_feed_failure: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className={darkMode ? "text-slate-300" : "text-slate-700"}>
                          📱 SMS on feed failure (critical) 
                          <span className={`text-xs ml-2 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Requires Twilio</span>
                        </span>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          aria-label="Enable email notifications on feed failure"
                          checked={alertSettings.email_on_feed_failure}
                          onChange={e => setAlertSettings(s => ({ ...s, email_on_feed_failure: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className={darkMode ? "text-slate-300" : "text-slate-700"}>
                          📧 Email on feed failure
                        </span>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          aria-label="Enable email notifications on manual source overdue"
                          checked={alertSettings.email_on_manual_overdue}
                          onChange={e => setAlertSettings(s => ({ ...s, email_on_manual_overdue: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className={darkMode ? "text-slate-300" : "text-slate-700"}>
                          📧 Email on manual source overdue ({alertSettings.manual_overdue_days}+ days)
                        </span>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          aria-label="Enable email on every new regulatory item"
                          checked={alertSettings.email_on_every_item}
                          onChange={e => setAlertSettings(s => ({ ...s, email_on_every_item: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className={darkMode ? "text-slate-300" : "text-slate-700"}>
                          📧 Email on every new regulatory item 
                          <span className="text-xs text-amber-500 ml-2">(not recommended - noisy)</span>
                        </span>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          aria-label="Enable daily digest email of new items"
                          checked={alertSettings.daily_digest_enabled}
                          onChange={e => setAlertSettings(s => ({ ...s, daily_digest_enabled: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className={darkMode ? "text-slate-300" : "text-slate-700"}>
                          📧 Daily digest of new items ({alertSettings.daily_digest_hour}:00 AM EST)
                        </span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Thresholds */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                        Consecutive Failures Threshold
                      </label>
                      <input
                        type="number"
                        aria-label="Consecutive failures threshold"
                        min="1"
                        max="10"
                        value={alertSettings.consecutive_failures_threshold}
                        onChange={e => setAlertSettings(s => ({ ...s, consecutive_failures_threshold: parseInt(e.target.value) || 3 }))}
                        className={`w-full px-3 py-2 rounded-lg border ${darkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-slate-200"}`}
                      />
                      <p className={`text-xs mt-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                        Alert after N consecutive poll failures
                      </p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                        Manual Overdue Days
                      </label>
                      <input
                        type="number"
                        aria-label="Manual overdue days threshold"
                        min="7"
                        max="90"
                        value={alertSettings.manual_overdue_days}
                        onChange={e => setAlertSettings(s => ({ ...s, manual_overdue_days: parseInt(e.target.value) || 14 }))}
                        className={`w-full px-3 py-2 rounded-lg border ${darkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-slate-200"}`}
                      />
                      <p className={`text-xs mt-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                        Alert if manual source not checked
                      </p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                        Daily Digest Hour (EST)
                      </label>
                      <select
                        aria-label="Select daily digest delivery hour"
                        value={alertSettings.daily_digest_hour}
                        onChange={e => setAlertSettings(s => ({ ...s, daily_digest_hour: parseInt(e.target.value) }))}
                        className={`w-full px-3 py-2 rounded-lg border ${darkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-slate-200"}`}
                      >
                        {[5, 6, 7, 8, 9].map(h => (
                          <option key={h} value={h}>{h}:00 AM</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* API Keys Notice */}
                  <div className={`p-4 rounded-lg border ${darkMode ? "bg-amber-900/20 border-amber-700" : "bg-amber-50 border-amber-200"}`}>
                    <h4 className={`font-medium mb-2 ${darkMode ? "text-amber-400" : "text-amber-700"}`}>
                      🔑 Required API Keys (Supabase Secrets)
                    </h4>
                    <div className={`text-sm ${darkMode ? "text-amber-300" : "text-amber-800"}`}>
                      <p className="mb-2">To enable notifications, add these secrets to your Supabase project:</p>
                      <ul className="list-disc list-inside space-y-1 font-mono text-xs">
                        <li>TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER (for SMS)</li>
                        <li>RESEND_API_KEY or SENDGRID_API_KEY (for email)</li>
                        <li>ALERT_FROM_EMAIL (optional, defaults to alerts@steadfastly.app)</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button 
                      variant="primary" 
                      onClick={saveAlertSettings}
                      disabled={alertSettingsLoading}
                    >
                      {alertSettingsLoading ? "Saving..." : "Save Alert Settings"}
                    </Button>
                  </div>
                </div>
              )}
              
              {!showAlertSettings && (
                <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                  {alertSettings.alert_email ? (
                    <div className="flex items-center gap-4">
                      <span>📧 {alertSettings.alert_email}</span>
                      {alertSettings.alert_sms && <span>📱 {alertSettings.alert_sms}</span>}
                      <Badge color="emerald" size="sm">Configured</Badge>
                    </div>
                  ) : (
                    <p>Click "Configure" to set up SMS/email alerts for feed failures.</p>
                  )}
                </div>
              )}
            </Card>
            )} {/* End isPlatformAdmin conditional for Alert Settings */}
            
            {/* Data Export Section - Available to all users */}
            <Card darkMode={darkMode} title="📦 Your Data, Your Rules" subtitle="Export your complete Steadfastly database at any time">
              <div className="p-4 bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl border border-emerald-200 mb-4">
                <p className="text-slate-700 mb-3">
                  Steadfastly believes your compliance data belongs to you. Export your complete database at any time:
                </p>
                <ul className="text-sm text-slate-600 space-y-1 mb-4">
                  <li>✓ Full history, not just current state</li>
                  <li>✓ Industry-standard formats (CSV, JSON)</li>
                  <li>✓ No lock-in, no restrictions</li>
                  <li>✓ Take it to a competitor if you want</li>
                </ul>
                <p className="text-sm text-slate-500 italic">We earn your business every month. Not by trapping you.</p>
              </div>
              <Button onClick={() => setShowDataExportModal(true)} className="flex items-center gap-2">
                <Icons.Download /> Export All Data
              </Button>
            </Card>

            {/* Setup Wizard - Re-run onboarding (ADMIN ONLY) */}
            {isAdmin && (
              <Card darkMode={darkMode} title="🚀 Setup Wizard" subtitle="Re-run the initial setup process (Admin only)">
                <div className={`p-4 rounded-xl border mb-4 ${darkMode ? "bg-indigo-900/20 border-indigo-800/50" : "bg-indigo-50 border-indigo-200"}`}>
                  <p className={`text-sm ${darkMode ? "text-indigo-200/80" : "text-indigo-800"}`}>
                    Run the setup wizard again to update company info, invite team members, or change your industry settings.
                  </p>
                </div>
                <Button 
                  variant="primary" 
                  onClick={() => {
                    // Clear the localStorage flag so wizard shows
                    if (orgId) {
                      localStorage.removeItem(`steadfastly_onboarding_${orgId}`);
                    }
                    setOnboardingModal(true);
                  }}
                >
                  <Icons.Settings className="w-4 h-4 mr-2" /> Run Setup Wizard
                </Button>
              </Card>
            )}

            {/* Synthetic Data Management - Training System */}
            <Card darkMode={darkMode} title="🎓 Training Data" subtitle="Learn by exploring - then wipe and start fresh" helpKey="syntheticData">
              <div className={`p-4 rounded-xl border mb-4 ${darkMode ? "bg-blue-900/20 border-blue-800/50" : "bg-blue-50 border-blue-200"}`}>
                <h4 className={`font-medium mb-2 ${darkMode ? "text-blue-300" : "text-blue-900"}`}>How Training Data Works</h4>
                <p className={`text-sm mb-3 ${darkMode ? "text-blue-200/80" : "text-blue-800"}`}>
                  Steadfastly comes pre-loaded with realistic sample data for your industry. Explore scenarios, 
                  thresholds, and incidents to learn how compliance documentation works. When you're ready, 
                  wipe the synthetic data and start fresh with your own.
                </p>
                <ul className={`text-sm space-y-1 ${darkMode ? "text-blue-200/70" : "text-blue-700"}`}>
                  <li>✓ Pre-configured for your vertical ({VERTICAL_CONFIG[vertical]?.displayName})</li>
                  <li>✓ Realistic scenarios with proper Nine Questions documentation</li>
                  <li>✓ Demonstrates what "examiner-ready" looks like</li>
                  <li>✓ One click to remove all training data</li>
                </ul>
              </div>
              
              <div className="flex items-center gap-4">
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    showConfirmModal({
                      title: "Load Training Data",
                      message: `Load sample training data for ${VERTICAL_CONFIG[vertical]?.displayName}? This will add realistic example scenarios and thresholds to help you learn the system.`,
                      confirmText: "Load Data",
                      confirmVariant: "primary",
                      onConfirm: async () => {
                        try {
                          setToast({ type: "info", message: "Loading training data..." });
                          const result = await callFn("load-synthetic-data", {
                            action: "load",
                            organization_id: orgId,
                            vertical,
                            language: userLanguage,
                          });
                          if (result.ok) {
                            setToast({ type: "success", message: result.message || "Training data loaded!" });
                            loadAllData();
                          } else {
                            setToast({ type: "error", message: result.error || "Failed to load training data" });
                          }
                        } catch (err) {
                          setToast({ type: "error", message: "Failed to load training data: " + sanitizeErrorMessage(err) });
                        }
                      }
                    });
                  }}
                >
                  Load Training Data
                </Button>
                
                <Button 
                  variant="danger" 
                  onClick={() => {
                    showConfirmModal({
                      title: "⚠️ Wipe All Training Data",
                      message: "This will permanently remove all sample/training data and cannot be undone. Your real data will be preserved.",
                      confirmText: "Yes, Wipe Data",
                      confirmVariant: "danger",
                      isDestructive: true,
                      onConfirm: async () => {
                        try {
                          setToast({ type: "info", message: "Wiping synthetic data..." });
                          const result = await callFn("load-synthetic-data", {
                            action: "wipe",
                            organization_id: orgId,
                          });
                          if (result.ok) {
                            setToast({ type: "success", message: "Training data removed! You're starting fresh." });
                            loadAllData();
                          } else {
                            setToast({ type: "error", message: result.error || "Failed to wipe data" });
                          }
                        } catch (err) {
                          setToast({ type: "error", message: "Failed to wipe data: " + sanitizeErrorMessage(err) });
                        }
                      }
                    });
                  }}
                >
                  🗑️ Wipe Training Data
                </Button>
              </div>
              
              <p className={`text-xs mt-3 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                Note: Only synthetic/training data will be removed. Your real compliance data is never affected.
              </p>
            </Card>

            {/* Language & Locale Settings */}
            <Card darkMode={darkMode} title="Language & Locale" subtitle="Configure display language and regional settings">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Display Language</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    value={userLanguage}
                    onChange={e => setUserLanguage(e.target.value)}
                  >
                    {Object.entries(LANGUAGE_CONFIG).map(([code, lang]) => (
                      <option key={code} value={code}>
                        {lang.nativeName} ({lang.name})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Changes the UI language. Some content may still appear in English.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Jurisdiction</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    value={jurisdiction}
                    onChange={e => setJurisdiction(e.target.value)}
                  >
                    {Object.entries(JURISDICTION_CONFIG).map(([code, config]) => (
                      <option key={code} value={code}>
                        {config.flag} {config.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Sets regulatory feeds, currency ({JURISDICTION_CONFIG[jurisdiction]?.symbol}), and date format.
                  </p>
                </div>
              </div>
              
              {/* Current Locale Preview */}
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Current Settings Preview</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Language:</span>
                    <span className="ml-2 font-medium">{LANGUAGE_CONFIG[userLanguage]?.nativeName}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Currency:</span>
                    <span className="ml-2 font-medium">{JURISDICTION_CONFIG[jurisdiction]?.symbol} ({JURISDICTION_CONFIG[jurisdiction]?.currency})</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Date Format:</span>
                    <span className="ml-2 font-medium">{JURISDICTION_CONFIG[jurisdiction]?.dateFormat}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Timezone:</span>
                    <span className="ml-2 font-medium">{JURISDICTION_CONFIG[jurisdiction]?.timezone}</span>
                  </div>
                </div>
              </div>
              
              {/* RTL Notice */}
              {LANGUAGE_CONFIG[userLanguage]?.direction === "rtl" && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <strong>Note:</strong> Arabic uses right-to-left (RTL) layout. Full RTL support is in progress.
                </div>
              )}
            </Card>
            
            {/* Security Settings - MFA */}
            <Card darkMode={darkMode} title="Security Settings" subtitle="Two-factor authentication and access controls">
              <div className="space-y-6">
                {/* MFA Status */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Icons.Shield className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">Two-Factor Authentication (2FA)</div>
                      <div className="text-sm text-slate-500">Add an extra layer of security to your account</div>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      try {
                        // Check if MFA is already enrolled
                        const { data: factors, error } = await supabase.auth.mfa.listFactors();
                        if (error) throw error;
                        
                        if (factors?.totp?.length > 0) {
                          // MFA is enabled - show option to disable via modal
                          const factorId = factors.totp[0].id;
                          showConfirmModal({
                            title: "Disable Two-Factor Authentication",
                            message: "Two-factor authentication is currently enabled. Disabling it will make your account less secure. Are you sure you want to continue?",
                            confirmText: "Disable 2FA",
                            confirmVariant: "danger",
                            isDestructive: true,
                            onConfirm: async () => {
                              const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId });
                              if (unenrollError) throw unenrollError;
                              setToast({ type: "success", message: "Two-factor authentication disabled" });
                              setTimeout(() => setToast(null), 3000);
                            }
                          });
                        } else {
                          // MFA not enabled - start enrollment
                          const { data: enrollData, error: enrollError } = await supabase.auth.mfa.enroll({
                            factorType: 'totp',
                            friendlyName: 'Steadfastly Authenticator',
                          });
                          if (enrollError) throw enrollError;
                          
                          // Show QR code in a new window
                          const qrWindow = window.open("", "_blank", "width=500,height=600");
                          qrWindow.document.write(`
                            <!DOCTYPE html>
                            <html>
                            <head>
                              <title>Set Up Two-Factor Authentication</title>
                              <style>
                                body { font-family: system-ui, sans-serif; text-align: center; padding: 40px; }
                                h1 { font-size: 24px; color: #1e293b; }
                                p { color: #64748b; margin: 16px 0; }
                                img { border: 8px solid #f1f5f9; border-radius: 12px; }
                                .code { font-family: monospace; background: #f1f5f9; padding: 8px 16px; border-radius: 8px; font-size: 14px; }
                              </style>
                            </head>
                            <body>
                              <h1>🔐 Set Up Two-Factor Authentication</h1>
                              <p>Scan this QR code with your authenticator app:</p>
                              <img src="${enrollData.totp.qr_code}" alt="QR Code" width="200" height="200" />
                              <p>Or enter this code manually:</p>
                              <div class="code">${enrollData.totp.secret}</div>
                              <p style="margin-top: 24px; color: #94a3b8; font-size: 12px;">
                                After scanning, enter the 6-digit code in your app to verify setup.<br/>
                                Then close this window and log in again.
                              </p>
                            </body>
                            </html>
                          `);
                          qrWindow.document.close();
                          setToast({ type: "success", message: "Scan the QR code with your authenticator app" });
                          setTimeout(() => setToast(null), 5000);
                        }
                      } catch (err) {
                        console.error("MFA error:", err);
                        setToast({ type: "error", message: `MFA setup error: ${sanitizeErrorMessage(err)}` });
                        setTimeout(() => setToast(null), 5000);
                      }
                    }}
                  >
                    Configure 2FA
                  </Button>
                </div>
                
                {/* Session Info */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-3">Session Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Logged in as:</span>
                      <span className="ml-2 font-medium">{session?.user?.email || "—"}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Last sign in:</span>
                      <span className="ml-2 font-medium">{session?.user?.last_sign_in_at ? formatDateTime(session.user.last_sign_in_at) : "—"}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Session expires:</span>
                      <span className="ml-2 font-medium">{session?.expires_at ? formatDateTime(new Date(session.expires_at * 1000)) : "—"}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Auth provider:</span>
                      <span className="ml-2 font-medium">{session?.user?.app_metadata?.provider || "email"}</span>
                    </div>
                  </div>
                </div>
                
                {/* Security Recommendations */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">🛡️ Security Best Practices</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>✓ Enable two-factor authentication for all BSA Officers</li>
                    <li>✓ Use strong, unique passwords</li>
                    <li>✓ Review the audit log regularly for suspicious activity</li>
                    <li>✓ Log out when using shared computers</li>
                  </ul>
                </div>
              </div>
            </Card>
            
            {/* User Management - Sprint 2 Feature */}
            <Card 
              title="Team & User Management" 
              subtitle="Manage team members, roles, and access"
              actions={
                <Button variant="primary" size="sm" onClick={() => setShowUserManagement(!showUserManagement)}>
                  <Icons.Users className="w-4 h-4 mr-1" />
                  {showUserManagement ? "Hide" : "Manage Team"}
                </Button>
              }
            >
              {showUserManagement && (
                <div className="space-y-6">
                  {/* Invite New User */}
                  <div className="bg-gradient-to-br from-slate-50 to-blue-50/30 border border-slate-200 rounded-xl p-5">
                    <h4 className="font-semibold text-slate-900 mb-4">Invite Team Member</h4>
                    <div className="flex flex-wrap gap-3">
                      <input
                        type="email"
                        placeholder="colleague@company.com"
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        className="flex-1 min-w-[200px] border border-blue-200 rounded px-3 py-2 text-sm"
                      />
                      <select
                        value={inviteRole}
                        onChange={e => setInviteRole(e.target.value)}
                        className="border border-blue-200 rounded px-3 py-2 text-sm"
                      >
                        <option value="analyst">Analyst</option>
                        <option value="officer">Compliance Officer</option>
                        <option value="admin">Admin</option>
                        <option value="readonly">Read-Only</option>
                      </select>
                      <Button 
                        variant="primary"
                        onClick={async () => {
                          if (!inviteEmail.includes("@")) {
                            setToast({ type: "error", message: "Please enter a valid email" });
                            setTimeout(() => setToast(null), 3000);
                            return;
                          }
                          // In production, this would send an invite via Supabase
                          setOrgMembers(prev => [...prev, {
                            user_id: `invited-${Date.now()}`,
                            role: inviteRole,
                            status: "invited",
                            profiles: { email: inviteEmail, full_name: inviteEmail.split("@")[0] }
                          }]);
                          setToast({ type: "success", message: `Invitation sent to ${inviteEmail}` });
                          setInviteEmail("");
                          setTimeout(() => setToast(null), 3000);
                        }}
                      >
                        Send Invite
                      </Button>
                    </div>
                  </div>
                  
                  {/* Team Members List */}
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Current Team ({orgMembers.length})</h4>
                    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                      {orgMembers.map(member => (
                        <div key={member.user_id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold">
                              {(member.profiles?.full_name || member.profiles?.email || "U").charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-slate-900">
                                {member.profiles?.full_name || member.profiles?.email?.split("@")[0] || "User"}
                              </div>
                              <div className="text-sm text-slate-500">{member.profiles?.email}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {member.status === "invited" && (
                              <Badge color="amber">Pending</Badge>
                            )}
                            <select
                              value={member.role || "analyst"}
                              onChange={(e) => {
                                setOrgMembers(prev => prev.map(m => 
                                  m.user_id === member.user_id ? { ...m, role: e.target.value } : m
                                ));
                                setToast({ type: "success", message: `Role updated to ${e.target.value}` });
                                setTimeout(() => setToast(null), 2000);
                              }}
                              className="border border-slate-200 rounded px-2 py-1 text-sm"
                              disabled={member.user_id === session?.user?.id}
                            >
                              <option value="analyst">Analyst</option>
                              <option value="officer">Compliance Officer</option>
                              <option value="admin">Admin</option>
                              <option value="readonly">Read-Only</option>
                            </select>
                            {member.user_id !== session?.user?.id && (
                              <button
                                onClick={() => {
                                  showConfirmModal({
                                    title: "Remove Team Member",
                                    message: `Are you sure you want to remove ${member.profiles?.email} from the team? They will lose access to this organization.`,
                                    confirmText: "Remove Member",
                                    confirmVariant: "danger",
                                    isDestructive: true,
                                    onConfirm: async () => {
                                      setOrgMembers(prev => prev.filter(m => m.user_id !== member.user_id));
                                      setToast({ type: "success", message: "Member removed" });
                                      setTimeout(() => setToast(null), 2000);
                                    }
                                  });
                                }}
                                aria-label={`Remove ${member.profiles?.email} from team`}
                                className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                                title="Remove member"
                              >
                                <Icons.X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Role Permissions Legend */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-700 mb-2 text-sm">Role Permissions</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-slate-600">
                      <div>
                        <span className="font-medium">Admin</span>
                        <p>Full access, user management</p>
                      </div>
                      <div>
                        <span className="font-medium">Compliance Officer</span>
                        <p>Attest, review, configure</p>
                      </div>
                      <div>
                        <span className="font-medium">Analyst</span>
                        <p>Add evidence, update scenarios</p>
                      </div>
                      <div>
                        <span className="font-medium">Read-Only</span>
                        <p>View only, no changes</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {!showUserManagement && (
                <div className="text-sm text-slate-500">
                  <p>Manage your compliance team members, assign roles, and control access permissions.</p>
                  <p className="mt-2">Current team: <strong>{orgMembers.length}</strong> member(s)</p>
                </div>
              )}
            </Card>
            
            {/* Admin Settings */}
            <Card darkMode={darkMode} title="Admin & Settings" subtitle="Organization settings, audit logs, and configuration">
              <div className="text-sm text-slate-500 mb-4">
                Manage organization configuration, roles, and system settings.
              </div>
              <AuditLogViewer auditLog={auditLog} orgId={orgId} onVerify={verifyAuditChain} chainStatus={chainStatus} isLoading={dataLoading} onExportCSV={exportAuditTrailCSV} />
            </Card>
            
            {/* SSO Configuration - Sprint 3 */}
            <Card 
              title="Single Sign-On (SSO)" 
              subtitle="Enterprise authentication with Azure AD, Okta, or SAML"
              actions={
                <div className="flex items-center gap-2">
                  <Badge color={
                    ssoConfig.status === "active" ? "emerald" :
                    ssoConfig.status === "configured" ? "blue" :
                    ssoConfig.status === "testing" ? "amber" : "slate"
                  }>
                    {ssoConfig.status === "active" ? "✓ Active" :
                     ssoConfig.status === "configured" ? "Configured" :
                     ssoConfig.status === "testing" ? "Testing..." : "Not Configured"}
                  </Badge>
                  <Button variant="secondary" size="sm" onClick={() => setShowSsoConfig(!showSsoConfig)}>
                    {showSsoConfig ? "Hide" : "Configure"}
                  </Button>
                </div>
              }
            >
              {showSsoConfig ? (
                <div className="space-y-6">
                  {/* Enable SSO Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-slate-900">Enable SSO for this organization</h4>
                      <p className="text-sm text-slate-500">Users with matching email domains will be redirected to SSO</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ssoConfig.enabled}
                        onChange={e => setSsoConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  
                  {ssoConfig.enabled && (
                    <>
                      {/* Provider Selection */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Identity Provider</label>
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { id: "azure_ad", name: "Azure AD", icon: "🔷", desc: "Microsoft Entra ID" },
                            { id: "okta", name: "Okta", icon: "🔐", desc: "Okta Identity" },
                            { id: "saml_generic", name: "Other SAML", icon: "🔑", desc: "Generic SAML 2.0" },
                          ].map(provider => (
                            <button
                              key={provider.id}
                              onClick={() => setSsoConfig(prev => ({ ...prev, provider: provider.id }))}
                              className={`p-4 border-2 rounded-lg text-left transition ${
                                ssoConfig.provider === provider.id
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-slate-200 hover:border-slate-300"
                              }`}
                            >
                              <div className="text-2xl mb-1">{provider.icon}</div>
                              <div className="font-medium text-slate-900">{provider.name}</div>
                              <div className="text-xs text-slate-500">{provider.desc}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Provider-specific fields */}
                      {ssoConfig.provider === "azure_ad" && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Azure AD Tenant ID</label>
                          <input
                            type="text"
                            value={ssoConfig.tenantId}
                            onChange={e => setSsoConfig(prev => ({ ...prev, tenantId: e.target.value }))}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                          />
                          <p className="text-xs text-slate-500 mt-1">Found in Azure Portal → Azure Active Directory → Overview</p>
                        </div>
                      )}
                      
                      {ssoConfig.provider === "okta" && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Okta Domain</label>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500">https://</span>
                            <input
                              type="text"
                              value={ssoConfig.oktaDomain}
                              onChange={e => setSsoConfig(prev => ({ ...prev, oktaDomain: e.target.value }))}
                              placeholder="your-company"
                              className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            />
                            <span className="text-sm text-slate-500">.okta.com</span>
                          </div>
                        </div>
                      )}
                      
                      {ssoConfig.provider === "saml_generic" && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">SAML Metadata XML</label>
                          <textarea
                            value={ssoConfig.metadataXml}
                            onChange={e => setSsoConfig(prev => ({ ...prev, metadataXml: e.target.value }))}
                            placeholder="Paste your IdP metadata XML here..."
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
                            rows={6}
                          />
                        </div>
                      )}
                      
                      {/* Email Domain */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Email Domain for SSO</label>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">@</span>
                          <input
                            type="text"
                            value={ssoConfig.domain}
                            onChange={e => setSsoConfig(prev => ({ ...prev, domain: e.target.value }))}
                            placeholder="yourcompany.com"
                            className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Users with this email domain will be redirected to SSO login</p>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                        <Button
                          variant="secondary"
                          onClick={testSsoConnection}
                          disabled={ssoConfig.status === "testing"}
                        >
                          {ssoConfig.status === "testing" ? (
                            <>
                              <Icons.Clock className="w-4 h-4 mr-1 animate-spin" />
                              Testing...
                            </>
                          ) : (
                            <>
                              <Icons.Link className="w-4 h-4 mr-1" />
                              Test Connection
                            </>
                          )}
                        </Button>
                        <Button
                          variant="primary"
                          onClick={saveSsoConfig}
                          disabled={!ssoConfig.domain || !ssoConfig.provider}
                        >
                          <Icons.Check className="w-4 h-4 mr-1" />
                          Save SSO Configuration
                        </Button>
                      </div>
                    </>
                  )}
                  
                  {/* SSO Info */}
                  <div className="bg-gradient-to-br from-slate-50 to-blue-50/20 border border-slate-200 rounded-xl p-4 text-sm">
                    <h4 className="font-medium text-slate-900 mb-2">How SSO Works</h4>
                    <ul className="text-slate-600 space-y-1 list-disc list-inside">
                      <li>Users enter their email on the login page</li>
                      <li>If the email domain matches, they're redirected to your IdP</li>
                      <li>After authentication, they're automatically logged into Steadfastly</li>
                      <li>New SSO users are automatically provisioned with Analyst role</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">
                  <p>Configure enterprise single sign-on to allow your team to authenticate using your organization's identity provider.</p>
                  <p className="mt-2">Supported providers: <strong>Azure AD</strong>, <strong>Okta</strong>, <strong>Generic SAML 2.0</strong></p>
                </div>
              )}
            </Card>
            
            {/* Scheduled Reports - Sprint 3 */}
            <Card 
              title="Scheduled Reports" 
              subtitle="Automated email reports for compliance status"
              actions={
                <Button variant="secondary" size="sm" onClick={() => setShowReportConfig(!showReportConfig)}>
                  {showReportConfig ? "Hide" : "Configure"}
                </Button>
              }
            >
              {showReportConfig ? (
                <div className="space-y-6">
                  {/* Add New Report */}
                  <div className="bg-gradient-to-br from-slate-50 to-emerald-50/30 border border-slate-200 rounded-xl p-5">
                    <h4 className="font-semibold text-slate-900 mb-4">Create Scheduled Report</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Report Name</label>
                        <input
                          id="report-name"
                          type="text"
                          placeholder="Weekly Compliance Summary"
                          className="w-full border border-emerald-200 rounded px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="report-type" className="block text-sm font-medium text-slate-700 mb-1">Report Type</label>
                        <select id="report-type" aria-label="Select report type" className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm transition-all duration-200 hover:border-emerald-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
                          <option value="summary">Compliance Summary</option>
                          <option value="regulatory">Regulatory Updates</option>
                          <option value="attestation">Attestation Status</option>
                          <option value="findings">Open Findings</option>
                          <option value="executive">Executive Dashboard</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="report-frequency" className="block text-sm font-medium text-slate-700 mb-1">Frequency</label>
                        <select id="report-frequency" aria-label="Select report frequency" className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm transition-all duration-200 hover:border-emerald-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly (Monday)</option>
                          <option value="biweekly">Bi-weekly</option>
                          <option value="monthly">Monthly (1st)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Recipients</label>
                        <input
                          id="report-recipients"
                          type="text"
                          placeholder="email@company.com, team@company.com"
                          className="w-full border border-emerald-200 rounded px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <Button
                        variant="primary"
                        onClick={() => {
                          const name = document.getElementById("report-name")?.value;
                          const type = document.getElementById("report-type")?.value;
                          const frequency = document.getElementById("report-frequency")?.value;
                          const recipients = document.getElementById("report-recipients")?.value;
                          if (name && recipients) {
                            addScheduledReport({ name, type, frequency, recipients: recipients.split(",").map(e => e.trim()) });
                            document.getElementById("report-name").value = "";
                            document.getElementById("report-recipients").value = "";
                          }
                        }}
                      >
                        <Icons.Clock className="w-4 h-4 mr-1" />
                        Schedule Report
                      </Button>
                    </div>
                  </div>
                  
                  {/* Existing Reports */}
                  {scheduledReports.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Active Reports ({scheduledReports.length})</h4>
                      <div className="space-y-2">
                        {scheduledReports.map(report => (
                          <div key={report.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${report.enabled ? "bg-emerald-500" : "bg-slate-300"}`} />
                              <div>
                                <div className="font-medium text-slate-900">{report.name}</div>
                                <div className="text-xs text-slate-500">
                                  {report.frequency} • {report.recipients?.length || 0} recipient(s) • Next: {formatDate(report.next_run)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleScheduledReport(report.id)}
                                className={`px-2 py-1 text-xs rounded ${report.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
                              >
                                {report.enabled ? "Enabled" : "Disabled"}
                              </button>
                              <button
                                onClick={() => deleteScheduledReport(report.id)}
                                aria-label={`Delete ${report.name} scheduled report`}
                                className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                              >
                                <Icons.X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-slate-500">
                  <p>Set up automated email reports to keep stakeholders informed about compliance status.</p>
                  <p className="mt-2">Active reports: <strong>{scheduledReports.length}</strong></p>
                </div>
              )}
            </Card>
            
            {/* API Access - Sprint 3 */}
            <Card 
              title="API Access" 
              subtitle="Programmatic access for integrations"
              actions={
                <Button variant="secondary" size="sm" onClick={() => setShowApiConfig(!showApiConfig)}>
                  {showApiConfig ? "Hide" : "Manage"}
                </Button>
              }
            >
              {showApiConfig ? (
                <div className="space-y-6">
                  {/* Create API Key */}
                  <div className="bg-gradient-to-br from-slate-50 to-indigo-50/30 border border-slate-200 rounded-xl p-5">
                    <h4 className="font-semibold text-slate-900 mb-4">Create API Key</h4>
                    <div className="flex flex-wrap gap-3">
                      <input
                        id="api-key-name"
                        type="text"
                        placeholder="Key name (e.g., Slack Integration)"
                        className="flex-1 min-w-[200px] border border-purple-200 rounded px-3 py-2 text-sm"
                      />
                      <select id="api-key-permissions" aria-label="Select API key permission level" className="border border-purple-200 rounded-xl px-4 py-2.5 text-sm transition-all duration-200 hover:border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20">
                        <option value="read">Read Only</option>
                        <option value="read,write">Read & Write</option>
                        <option value="read,write,admin">Full Access</option>
                      </select>
                      <Button
                        variant="primary"
                        onClick={() => {
                          const name = document.getElementById("api-key-name")?.value;
                          const permissions = document.getElementById("api-key-permissions")?.value?.split(",") || ["read"];
                          if (name) {
                            createApiKey(name, permissions);
                            document.getElementById("api-key-name").value = "";
                          }
                        }}
                      >
                        Generate Key
                      </Button>
                    </div>
                  </div>
                  
                  {/* Existing API Keys */}
                  {apiKeys.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Active API Keys ({apiKeys.length})</h4>
                      <div className="space-y-2">
                        {apiKeys.map(key => (
                          <div key={key.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Icons.Key className="w-5 h-5 text-purple-500" />
                              <div>
                                <div className="font-medium text-slate-900 flex items-center gap-2">
                                  {key.name}
                                  {key.key_full && (
                                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">NEW - Copy now!</span>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500 font-mono">
                                  {key.key_full || key.key_prefix}
                                  {key.key_full && (
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(key.key_full);
                                        setToast({ type: "success", message: "API key copied to clipboard" });
                                        setTimeout(() => setToast(null), 2000);
                                        // Remove full key from display after copy
                                        setApiKeys(prev => prev.map(k => k.id === key.id ? { ...k, key_full: null } : k));
                                      }}
                                      className="ml-2 text-blue-600 hover:text-blue-800"
                                    >
                                      Copy
                                    </button>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                  Created {formatDate(key.created_at)} • {key.permissions?.join(", ")} • {key.last_used_at ? `Last used ${formatDate(key.last_used_at)}` : "Never used"}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                showConfirmModal({
                                  title: "Revoke API Key",
                                  message: `Are you sure you want to revoke the API key "${key.name}"? Any applications using this key will stop working immediately. This cannot be undone.`,
                                  confirmText: "Revoke Key",
                                  confirmVariant: "danger",
                                  isDestructive: true,
                                  onConfirm: async () => {
                                    await revokeApiKey(key.id);
                                  }
                                });
                              }}
                              aria-label={`Revoke API key ${key.name}`}
                              className="text-red-500 hover:text-red-700 px-3 py-1.5 text-sm border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              Revoke
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* API Documentation */}
                  <div className="bg-slate-100 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-2">API Endpoints</h4>
                    <div className="text-xs font-mono text-slate-600 space-y-1">
                      <div><span className="text-emerald-600">GET</span> /api/v1/scenarios</div>
                      <div><span className="text-emerald-600">GET</span> /api/v1/thresholds</div>
                      <div><span className="text-emerald-600">GET</span> /api/v1/attestations</div>
                      <div><span className="text-emerald-600">GET</span> /api/v1/regulatory-updates</div>
                      <div><span className="text-blue-600">POST</span> /api/v1/evidence/upload</div>
                      <div><span className="text-amber-600">PATCH</span> /api/v1/scenarios/:id</div>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">
                      Include your API key in the <code className="bg-slate-200 px-1 rounded">Authorization</code> header: <code className="bg-slate-200 px-1 rounded">Bearer sf_xxx...</code>
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">
                  <p>Create API keys for integrations with SIEM, ticketing systems, or custom workflows.</p>
                  <p className="mt-2">Active keys: <strong>{apiKeys.length}</strong></p>
                </div>
              )}
            </Card>
            
            {/* ================================================================= */}
            {/* SPRINT 4: Webhooks, Branding, Analytics, Offline, Gap Detector */}
            {/* ================================================================= */}
            
            {/* Webhook Integrations */}
            <Card 
              title="🔗 Webhook Integrations" 
              subtitle="Route alerts to Slack, Teams, or ServiceNow"
              actions={
                <Button variant="primary" size="sm" onClick={() => { setEditingWebhook(null); setShowWebhookModal(true); }}>
                  + Add Webhook
                </Button>
              }
            >
              {webhooks.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Icons.Webhook className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No webhooks configured</p>
                  <p className="text-sm mt-1">Add integrations to receive alerts in your tools</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {webhooks.map(webhook => (
                    <div key={webhook.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${webhook.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                        <div>
                          <div className="font-medium text-slate-900 flex items-center gap-2">
                            {webhook.provider === "slack" && "🟢"}
                            {webhook.provider === "teams" && "🔵"}
                            {webhook.provider === "servicenow" && "⚪"}
                            {webhook.provider === "generic" && "🔗"}
                            {webhook.name}
                            {!webhook.is_active && <Badge color="slate">OFF</Badge>}
                            {webhook.failure_count >= 3 && <Badge color="red">Failing</Badge>}
                          </div>
                          <div className="text-xs text-slate-500">
                            Events: {webhook.events?.slice(0, 3).map(e => WEBHOOK_EVENTS.find(we => we.id === e)?.label || e).join(", ")}
                            {webhook.events?.length > 3 && ` +${webhook.events.length - 3} more`}
                          </div>
                          <div className="text-xs text-slate-500">
                            Last triggered: {webhook.last_triggered_at ? formatDateTime(webhook.last_triggered_at) : "Never"}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="secondary" size="sm" onClick={() => testWebhook(webhook)}>Test</Button>
                        <Button variant="secondary" size="sm" onClick={() => { setEditingWebhook(webhook); setShowWebhookModal(true); }}>Edit</Button>
                        <button
                          onClick={() => openDeleteModal("webhook", webhook)}
                          aria-label={`Delete ${webhook.name} webhook`}
                          className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          <Icons.X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            
            {/* Custom Branding */}
            <Card 
              title="🎨 Organization Branding" 
              subtitle="Logo, colors, and white-label customization"
              actions={
                <Button variant="secondary" size="sm" onClick={() => setShowBrandingConfig(!showBrandingConfig)}>
                  {showBrandingConfig ? "Hide" : "Customize"}
                </Button>
              }
            >
              {showBrandingConfig ? (
                <div className="space-y-6">
                  {/* Logo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Organization Logo</label>
                    <div className="flex items-center gap-4">
                      <div className="w-48 h-12 bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center">
                        {branding.logo_url ? (
                          <img src={branding.logo_url} alt="Logo" className="max-h-10 max-w-44" />
                        ) : (
                          <span className="text-slate-500 text-sm">No logo</span>
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          accept="image/png,image/svg+xml,image/jpeg"
                          className="hidden"
                          id="logo-upload"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = () => setBranding(prev => ({ ...prev, logo_url: reader.result }));
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                        <label htmlFor="logo-upload" className="cursor-pointer inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-600 rounded text-sm hover:bg-blue-100">
                          Upload Logo
                        </label>
                        {branding.logo_url && (
                          <button 
                            onClick={() => setBranding(prev => ({ ...prev, logo_url: null }))}
                            className="ml-2 text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        )}
                        <p className="text-xs text-slate-500 mt-1">PNG or SVG, max 2MB</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Brand Colors */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-3">Brand Colors</label>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { key: "primary_color", label: "Primary Color", desc: "Headers, buttons" },
                        { key: "secondary_color", label: "Accent Color", desc: "Links, highlights" },
                        { key: "header_bg", label: "Header Background", desc: "Top bar" },
                        { key: "sidebar_bg", label: "Sidebar Background", desc: "Navigation" },
                      ].map(({ key, label, desc }) => (
                        <div key={key} className="flex items-center gap-3">
                          <input
                            type="color"
                            value={branding[key]}
                            onChange={(e) => setBranding(prev => ({ ...prev, [key]: e.target.value }))}
                            className="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                          />
                          <div>
                            <div className="text-sm font-medium text-slate-700">{label}</div>
                            <div className="text-xs text-slate-500">{desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Preview */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="text-xs text-slate-500 px-3 py-1 bg-slate-50 border-b">Preview</div>
                    <div style={{ backgroundColor: branding.header_bg }} className="p-3 flex items-center gap-3">
                      {branding.logo_url ? (
                        <img src={branding.logo_url} alt="Logo" className="h-6" />
                      ) : (
                        <div style={{ backgroundColor: branding.secondary_color }} className="w-8 h-8 rounded text-white flex items-center justify-center font-bold">S</div>
                      )}
                      <span className="text-white font-medium">Dashboard</span>
                      <span className="text-slate-300">Scenarios</span>
                      <span className="text-slate-300">Settings</span>
                    </div>
                  </div>
                  
                  {/* Options */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={branding.show_steadfastly_badge}
                        onChange={(e) => setBranding(prev => ({ ...prev, show_steadfastly_badge: e.target.checked }))}
                        className="rounded"
                      />
                      Show "Powered by Steadfastly" badge
                    </label>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                    <Button variant="secondary" onClick={resetBranding}>Reset to Default</Button>
                    <Button variant="primary" onClick={() => saveBranding(branding)}>Save Branding</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-6">
                  <div className="flex-1">
                    <p className="text-sm text-slate-500">Customize Steadfastly with your organization's logo and brand colors.</p>
                    <p className="text-sm text-slate-500 mt-1">Branding appears in: App header, Board reports, Examiner packages</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: branding.primary_color }} />
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: branding.secondary_color }} />
                  </div>
                </div>
              )}
            </Card>
            
            {/* Advanced Analytics */}
            <Card 
              title="📊 Advanced Analytics" 
              subtitle="Risk scoring, heat maps, and exam readiness"
            >
              <div className="space-y-6">
                {/* Risk Score Gauge */}
                {(() => {
                  const risk = calculateRiskScore();
                  return (
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-slate-900">🎯 Governance Risk Score</h4>
                        <Badge color={risk.level === "LOW" ? "emerald" : risk.level === "MODERATE" ? "amber" : "red"}>
                          {risk.level} RISK
                        </Badge>
                      </div>
                      <div className="flex items-center gap-8">
                        <div className="text-center">
                          <div className="text-5xl font-bold" style={{ color: risk.total >= 80 ? "#10B981" : risk.total >= 60 ? "#F59E0B" : "#EF4444" }}>
                            {risk.total}
                          </div>
                          <div className="text-sm text-slate-500">out of 100</div>
                        </div>
                        <div className="flex-1 space-y-2">
                          {Object.entries(risk.scores).map(([key, score]) => (
                            <div key={key} className="flex items-center gap-2">
                              <div className="w-32 text-xs text-slate-600 truncate">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${score >= 80 ? "bg-emerald-500" : score >= 60 ? "bg-amber-500" : "bg-red-500"}`}
                                  style={{ width: `${score}%` }}
                                />
                              </div>
                              <div className="w-10 text-xs text-right text-slate-500">{Math.round(score)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                
                {/* Exam Readiness */}
                {(() => {
                  const exam = calculateExamReadiness();
                  return (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-slate-900">🎯 Exam Readiness Score</h4>
                        <Badge color={exam.level === "EXAM READY" ? "emerald" : exam.level === "NEEDS ATTENTION" ? "amber" : "red"}>
                          {exam.level}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-6 mb-4">
                        <div className="relative w-24 h-24">
                          <svg className="w-24 h-24 transform -rotate-90">
                            <circle cx="48" cy="48" r="40" fill="none" stroke="#E2E8F0" strokeWidth="8" />
                            <circle 
                              cx="48" cy="48" r="40" fill="none" 
                              stroke={exam.score >= 85 ? "#10B981" : exam.score >= 70 ? "#F59E0B" : "#EF4444"}
                              strokeWidth="8" 
                              strokeDasharray={`${exam.score * 2.51} 251`}
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-2xl font-bold text-slate-900">{exam.score}%</span>
                          </div>
                        </div>
                        <div className="flex-1 space-y-1">
                          {exam.checks.map((check, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <span>{check.status === "pass" ? "✅" : check.status === "warn" ? "⚠️" : "❌"}</span>
                              <span className={check.status === "pass" ? "text-slate-600" : check.status === "warn" ? "text-amber-700" : "text-red-700"}>
                                {check.text}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 pt-3 border-t border-blue-200">
                        Estimated exam prep time: <strong>12 hours</strong> (vs. 80 hrs industry avg)
                      </div>
                    </div>
                  );
                })()}
                
                {/* Heat Map */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-4">📊 Compliance Heat Map</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          <th className="text-left py-2 px-2 text-slate-600"></th>
                          <th className="py-2 px-2 text-slate-600 text-center">BSA/AML</th>
                          <th className="py-2 px-2 text-slate-600 text-center">Fair Lending</th>
                          <th className="py-2 px-2 text-slate-600 text-center">Vendor</th>
                          <th className="py-2 px-2 text-slate-600 text-center">Privacy</th>
                          <th className="py-2 px-2 text-slate-600 text-center">Cyber</th>
                        </tr>
                      </thead>
                      <tbody>
                        {["Scenarios", "Thresholds", "Reviews", "Evidence"].map(row => (
                          <tr key={row}>
                            <td className="py-2 px-2 text-slate-700 font-medium">{row}</td>
                            {[98, 95, 82, 91, 78].map((score, i) => {
                              // Vary scores slightly per row
                              const adjusted = Math.max(50, Math.min(100, score + (Math.random() * 20 - 10)));
                              const color = adjusted >= 90 ? "bg-emerald-500" : adjusted >= 70 ? "bg-amber-500" : "bg-red-500";
                              return (
                                <td key={i} className="py-2 px-2 text-center">
                                  <div className={`inline-flex items-center justify-center w-12 h-8 rounded ${color} text-white text-xs font-medium`}>
                                    {Math.round(adjusted)}%
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-center gap-6 mt-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded" /> ≥90%</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 rounded" /> 70-89%</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded" /> &lt;70%</span>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Governance Gap Detector */}
            <Card 
              title="📋 Governance Gaps" 
              subtitle="Facts, not predictions. What's incomplete."
              actions={
                <div className="flex items-center gap-2">
                  {governanceGaps.length > 0 && (
                    <Badge color="red">{governanceGaps.length} gaps</Badge>
                  )}
                  <Button variant="secondary" size="sm" onClick={() => setShowGapsPanel(!showGapsPanel)}>
                    {showGapsPanel ? "Hide" : "View All"}
                  </Button>
                </div>
              }
            >
              {showGapsPanel ? (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="font-medium text-amber-900">
                      {governanceGaps.length} items require attention before your next attestation
                    </p>
                  </div>
                  
                  {/* Overdue Reviews */}
                  {(() => {
                    const overdueGaps = governanceGaps.filter(g => g.type === "OVERDUE_REVIEW");
                    if (overdueGaps.length === 0) return null;
                    return (
                      <div>
                        <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                          🔴 OVERDUE REVIEWS ({overdueGaps.length})
                        </h4>
                        <div className="space-y-2">
                          {overdueGaps.slice(0, 3).map((gap, i) => (
                            <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <div className="font-medium text-slate-900">{gap.item.name}</div>
                              <div className="text-sm text-red-700">{gap.fact}</div>
                              <Button variant="secondary" size="sm" className="mt-2" onClick={() => {
                                if (gap.itemType === "scenario") {
                                  setActiveTab("scenarios");
                                } else {
                                  setActiveTab("thresholds");
                                }
                              }}>
                                Review Now →
                              </Button>
                            </div>
                          ))}
                          {overdueGaps.length > 3 && (
                            <button className="text-sm text-red-600 hover:text-red-800">
                              Show {overdueGaps.length - 3} more overdue items...
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Incomplete Nine Questions */}
                  {(() => {
                    const eqGaps = governanceGaps.filter(g => g.type === "INCOMPLETE_NINE_QUESTIONS");
                    if (eqGaps.length === 0) return null;
                    return (
                      <div>
                        <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                          🟡 INCOMPLETE NINE QUESTIONS ({eqGaps.length})
                        </h4>
                        <div className="space-y-2">
                          {eqGaps.slice(0, 3).map((gap, i) => (
                            <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <div className="font-medium text-slate-900">{gap.item.name}</div>
                              <div className="text-sm text-amber-700">Missing: {gap.missing?.join(", ")}</div>
                              <div className="text-xs text-slate-500">Complete: {gap.complete}/8 questions</div>
                              <Button variant="secondary" size="sm" className="mt-2">Complete Now →</Button>
                            </div>
                          ))}
                          {eqGaps.length > 3 && (
                            <button className="text-sm text-amber-600 hover:text-amber-800">
                              Show {eqGaps.length - 3} more incomplete items...
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Unreviewed Regulatory */}
                  {(() => {
                    const regGaps = governanceGaps.filter(g => g.type === "UNREVIEWED_REGULATORY");
                    if (regGaps.length === 0) return null;
                    return (
                      <div>
                        <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                          🔵 UNREVIEWED CRITICAL/HIGH UPDATES ({regGaps.length})
                        </h4>
                        <div className="space-y-2">
                          {regGaps.slice(0, 3).map((gap, i) => (
                            <div key={i} className={`border rounded-xl p-4 ${gap.severity === "BLOCKING" ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}>
                              <div className="font-medium text-slate-900">{gap.item.title}</div>
                              <div className="text-sm text-blue-700">{gap.fact}</div>
                              {gap.severity === "BLOCKING" && (
                                <Badge color="red" className="mt-1">BLOCKING ATTESTATION</Badge>
                              )}
                              <Button variant="secondary" size="sm" className="mt-2" onClick={() => setActiveTab("regulatory")}>
                                Review Now →
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Attestation Blocker Summary */}
                  {(() => {
                    const { canSign, blockers, message } = canSignAttestationGaps();
                    return (
                      <div className={`rounded-lg p-4 ${canSign ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
                        <h4 className="font-semibold mb-2">{canSign ? "✅ Ready to Attest" : "🚫 Attestation Blocked"}</h4>
                        <p className="text-sm text-slate-700">{message}</p>
                        {!canSign && blockers.length > 0 && (
                          <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                            {blockers.slice(0, 5).map((b, i) => (
                              <li key={i}>{b.item.name || b.item.title}: {b.fact}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">
                      {governanceGaps.length === 0 
                        ? "No gaps detected. Your governance program is complete!"
                        : `${governanceGaps.length} items need attention. Click "View All" to see details.`
                      }
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {governanceGaps.filter(g => g.type === "OVERDUE_REVIEW").length > 0 && (
                      <Badge color="red">{governanceGaps.filter(g => g.type === "OVERDUE_REVIEW").length} overdue</Badge>
                    )}
                    {governanceGaps.filter(g => g.type === "INCOMPLETE_NINE_QUESTIONS").length > 0 && (
                      <Badge color="amber">{governanceGaps.filter(g => g.type === "INCOMPLETE_NINE_QUESTIONS").length} incomplete</Badge>
                    )}
                  </div>
                </div>
              )}
            </Card>
            
            {/* Offline Mode Status */}
            <Card 
              title={isOffline ? "📴 Offline Mode" : "📶 Connection Status"} 
              subtitle={isOffline ? "Changes will sync when connected" : "You're online"}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isOffline ? (
                    <Icons.WifiOff className="w-8 h-8 text-amber-500" />
                  ) : (
                    <Icons.Wifi className="w-8 h-8 text-emerald-500" />
                  )}
                  <div>
                    <p className="font-medium text-slate-900">
                      {isOffline ? "Working Offline" : "Connected"}
                    </p>
                    <p className="text-sm text-slate-500">
                      {isOffline 
                        ? `${pendingOfflineChanges.length} changes pending sync`
                        : "All changes saved"
                      }
                    </p>
                  </div>
                </div>
                {pendingOfflineChanges.length > 0 && (
                  <Button variant="secondary" size="sm">
                    View Pending ({pendingOfflineChanges.length})
                  </Button>
                )}
              </div>
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <h4 className="text-sm font-medium text-slate-700 mb-2">What works offline:</h4>
                <ul className="text-xs text-slate-600 space-y-1">
                  <li>✅ View all cached scenarios, thresholds, regulatory updates</li>
                  <li>✅ View examiner packages (if previously generated)</li>
                  <li>✅ Add notes and comments (queued for sync)</li>
                  <li>✅ Mark items as reviewed (queued for sync)</li>
                  <li>❌ Generate new examiner packages</li>
                  <li>❌ Sign attestations</li>
                </ul>
              </div>
            </Card>
          </div>
    </>
  );
}

export default AdminTab;
